import { FilledInput } from '@/Components/FilledInput/FilledInput';
import Input from '@/Components/Input/Input';
import icnClose from '@icons/icnClose.png';
import { useEffect, useRef, useState, type FocusEventHandler, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import Styles from './MultiSelect.module.scss';

export interface MultiSelectOption {
    Value: string;
    ID: number | string;
}

export interface MultiSelectProps {
    title: string;
    onChange: (selectedOptions: MultiSelectOption[]) => void;
    options?: MultiSelectOption[];
    async?: boolean;
    apiCall?: (searchTerm: string) => Promise<MultiSelectOption[]>;
    selected: MultiSelectOption[];
    hiddenOptions?: MultiSelectOption[];
    leftAlign?: boolean;
    useContains?: boolean;
}

const filterList = (options: MultiSelectOption[] | undefined, hiddenOptions: MultiSelectOption[] | undefined, selected: MultiSelectOption[], searchTerm: string, useContains: boolean): MultiSelectOption[] => {
    let filteredOptions = [...options ?? []];

    filteredOptions = filteredOptions.filter((option) => !selected.map((selected) => selected.Value).includes(option.Value));

    if (hiddenOptions) {
        filteredOptions = filteredOptions.filter((option) => !hiddenOptions.map((selected) => selected.Value).includes(option.Value));
    }

    if (!searchTerm) {
        return filteredOptions;
    }

    if (useContains) {
        return filteredOptions.filter((option) => option.Value.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    return filteredOptions.filter((option) => option.Value.localeCompare(searchTerm, undefined, { sensitivity: 'accent' }) === 0);
};

function MultiSelect(props: Readonly<MultiSelectProps>): React.ReactElement {
    const { t } = useTranslation();
    const { onChange, async, apiCall, title, options, selected, hiddenOptions, useContains } = props;
    const input = useRef<HTMLInputElement>(null);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const blurTimeout = useRef<number | null>(null);

    const [open, setOpen] = useState<boolean>(false);

    const filteredItems = filterList(options, hiddenOptions, selected, searchTerm, useContains ?? true);

    useEffect(() => {
        if (!async) {
            return;
        }

        if (apiCall === undefined) {
            return console.error('When MultiSelect is async, you need to also provide an apiURL to get search results');
        }

        return console.error('Async functionality is not implemented yet! Please use "options" to preload data');

        // NOTE: This needs stateFull "allItems" instead of using the options prop
        // void apiCall(searchTerm)
        //     .then(res => {
        //         setAllItems(res);
        //     });
    }, [searchTerm]);

    const setFocusAfterInteraction = (): void => {
        // When making changes to the selected options, we want to make sure input focus is kept
        // setTimeout is necessary to be AFTER the react rerender because the first time, the input element might change.
        // Cannot handle this securely in a useEffect callback sadly
        setTimeout(() => {
            if (input.current != null) {
                input.current.focus();
            }
        }, 0);
    };

    const addChosen = (event: React.MouseEvent<HTMLDivElement> | null, item: MultiSelectOption): void => {
        if (event) {
            event.stopPropagation();
            event.preventDefault();
        }

        onChange([...selected, item]);

        setSearchTerm('');
        setFocusAfterInteraction();
    };

    const removeChosen = (item: MultiSelectOption): void => {
        onChange(selected.filter((x) => x.ID !== item.ID));

        setFocusAfterInteraction();
    };

    const onFocus = (): void => {
        if (blurTimeout.current) {
            clearTimeout(blurTimeout.current);
        }

        if (!async || searchTerm !== '') {
            setOpen(true);
        }
    };

    const onBlurInput = (_: React.FocusEvent<HTMLInputElement>): void => {
        blurTimeout.current = window.setTimeout(() => {
            setSearchTerm('');
            setOpen(false);
        }, 150);
    };

    const onKeyUp = (e: React.KeyboardEvent<HTMLInputElement>): void => {
        // On enter, add searched single result to selected items
        if (e.key === 'Enter' && filteredItems.length === 1) {
            addChosen(null, filteredItems[0]);
        }

        // On backspace, when search is empty, remove the newest item from the selected list
        if (e.key === 'Backspace' && searchTerm === '' && selected.length > 0) {
            selected.splice(-1);
            onChange(selected);
        }
    };

    if (!props.leftAlign) {
        return (
            <div className={Styles.multiSelectContainer}>
                {selected.length > 0 && <div className={Styles.title}>{title}</div>}
                <SelectedOptions selected={selected} removeChosen={removeChosen} />
                <Selection
                    hasSelected={selected.length > 0}
                    open={open}
                    filteredItems={filteredItems}
                    input={input}
                    onBlurInput={onBlurInput}
                    title={selected.length > 0 ? t('Search') + '...' : title}
                    searchTerm={searchTerm}
                    addChosen={addChosen}
                    onFocus={onFocus}
                    setSearchTerm={setSearchTerm}
                    onKeyUp={onKeyUp}
                />
            </div>
        );
    }

    return (
        <div className={Styles.multiSelectContainer}>
            {selected.length > 0 && <div className={Styles.title}>{title}</div>}
            <div className={Styles.leftAlign}>
                <Selection
                    hasSelected={selected.length > 0}
                    open={open}
                    filteredItems={filteredItems}
                    input={input}
                    onBlurInput={onBlurInput}
                    title={selected.length > 0 ? t('Search') + '...' : title}
                    searchTerm={searchTerm}
                    addChosen={addChosen}
                    onFocus={onFocus}
                    setSearchTerm={setSearchTerm}
                    onKeyUp={onKeyUp}
                />
                <SelectedOptions selected={selected} removeChosen={removeChosen} />
            </div>
        </div>
    );
}

interface SelectedProps {
    selected: MultiSelectOption[];
    removeChosen: (option: MultiSelectOption) => void;
}

const SelectedOptions = ({ selected, removeChosen }: Readonly<SelectedProps>): React.ReactElement => {
    if (selected.length === 0) {
        return <></>;
    }

    return (
        <div className={Styles.optionsContainer}>
            {selected.map((x) => (
                <div className={Styles.chosenOption} onClick={() => removeChosen(x)} key={x.ID}>
                    <div>{x.Value}</div>
                    <img alt="close" src={icnClose} />
                </div>
            ))}
        </div>
    );
};

interface SelectionProps {
    filteredItems: MultiSelectOption[];
    hasSelected: boolean;
    input: Ref<HTMLInputElement>;
    onBlurInput: FocusEventHandler<HTMLInputElement>;
    title: string;
    searchTerm: string;
    setSearchTerm: Function;
    addChosen: Function;
    onFocus: Function;
    onKeyUp?: Function;
    open: boolean;
}

const Selection = ({
    filteredItems,
    hasSelected,
    open,
    input,
    onFocus,
    onBlurInput,
    title,
    searchTerm,
    setSearchTerm,
    addChosen,
    onKeyUp,
}: Readonly<SelectionProps>): React.ReactElement => {
    const { t } = useTranslation();

    return (
        <div className={Styles.multiSelect}>
            {/* Show dynamic input version with animation for first use */}
            {!hasSelected && (
                <Input
                    ref={input}
                    onBlur={onBlurInput}
                    label={title}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => onFocus()}
                    onKeyUp={(e) => onKeyUp?.(e)}
                />
            )}
            {/* Use placeholder version of input so it doesn't shift anymore after initial selection */}
            {hasSelected && (
                <FilledInput
                    small
                    dark
                    ref={input}
                    placeholder={title}
                    onBlur={onBlurInput}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onFocus={() => onFocus()}
                    onKeyUp={(e) => onKeyUp?.(e)}
                />
            )}
            <div className={Styles.optionsListContainer + ' ' + (open ? Styles.listOpen : '')}>
                <div className={Styles.optionsContainer}>
                    {filteredItems.length > 0
                        ? (
                                filteredItems.slice(0, 50).map((i) => (
                                    <div key={i.ID} onClick={(e) => addChosen(e, i)}>
                                        {i.Value}
                                    </div>
                                ))
                            )
                        : (
                                <div>{t('NoResultsFound')}</div>
                            )}
                    {filteredItems.length > 50 && <div>...</div>}
                </div>
            </div>
        </div>
    );
};

export default MultiSelect;
